import { useMemo } from 'react';
import {
  Box,
  DataTable,
  TabNav,
  TabNavList,
  TabNavTab,
  type DataTableFeatures,
} from '@customerio/pluma-components/react';
import type { ColumnDef } from '@tanstack/react-table';
import { useLocation } from '@/services/router/compat';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { DataIndexTableFrame, useSearchDraft } from '../components/table-shell';
import type { Conversion } from './adapter';
import {
  ConversionCountCell,
  ConversionCountedWhenCell,
  ConversionImpactCell,
  ConversionNameCell,
  ConversionsCountNote,
  ConversionStatusCell,
  ConversionUsageCell,
  type ConversionUsageTerms,
} from './cells';
import {
  CONVERSIONS_PAGE_SIZE,
  CONVERSION_VIEWS,
  conversionViewLabel,
  type ConversionView,
  type ConversionViewCounts,
} from './rows';
import { goalPath } from './urls';

/**
 * The workspace's conversions, one row per goal.
 *
 * The layout and copy are the spike's (`customerio/ui#16717`,
 * `app/components/data-index-conversions-table`); the rows come from the goals
 * list rather than from definitions built in the browser, so the table itself
 * neither groups nor de-duplicates anything.
 */

/** How many days the count column covers, once AUD-4576 fills it in. */
export const CONVERSION_COUNT_DAYS = 30;

export type ConversionsTableProps = {
  workspaceId: number;
  /** The rows on the current page, already searched and paged. */
  rows: Conversion[];
  /** How many rows the search left, which is what the pager counts. */
  total: number;
  isLoading: boolean;
  search: string;
  onSearchChange: (search: string) => void;
  view: ConversionView;
  viewCounts: ConversionViewCounts;
  page: number;
  onPageChange: (page: number) => void;
};

export function ConversionsTable({
  workspaceId,
  rows,
  total,
  isLoading,
  search,
  onSearchChange,
  view,
  viewCounts,
  page,
  onPageChange,
}: ConversionsTableProps) {
  const taxonomy = useProductTaxonomy();
  const location = useLocation();
  const draft = useSearchDraft(search);

  const terms = useMemo(
    () => ({
      campaignSingular: taxonomy.term('campaigns.singular'),
      newsletterSingular: taxonomy.term('broadcasts.singular'),
    }),
    [taxonomy],
  );
  const columns = useConversionColumns(String(workspaceId), terms);
  // Both nouns are renameable, so they are read rather than written: a
  // workspace that calls campaigns something else would otherwise be told to
  // set a conversion on a thing it does not have.
  const emptyStateDescription = `Conversions are the goals this workspace tracks. Set one on ${taxonomy.term(
    'campaigns.indefiniteSingular',
  )} or ${taxonomy.term('broadcasts.indefiniteSingular')}, or create one from the Goals page.`;

  return (
    <>
      <Box mb="150">
        <TabNav aria-label="Conversion views">
          <TabNavList>
            {CONVERSION_VIEWS.map((nextView) => (
              <TabNavTab
                key={nextView}
                href={conversionViewHref(location.pathname, location.searchStr, nextView)}
                isActive={view === nextView}
              >
                {`${conversionViewLabel(nextView)} (${String(viewCounts[nextView])})`}
              </TabNavTab>
            ))}
          </TabNavList>
        </TabNav>
      </Box>
      <DataTable
        caption="Conversions"
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        layout="fixed"
        withSearch
        search={{
          value: search,
          inputValue: draft.text,
          onSearchInput: draft.setText,
          onSearch: onSearchChange,
          shouldSearchOnEnter: true,
          shouldSearchOnClear: true,
          isManual: true,
        }}
        withPagination
        pagination={{
          page,
          pageSize: CONVERSIONS_PAGE_SIZE,
          totalItemCount: total,
          onPageChange,
          isManual: true,
        }}
        emptyState={{
          title: 'No conversions yet',
          description: emptyStateDescription,
          icon: 'data-index',
        }}
      >
        <DataIndexTableFrame />
        <Box mt="200" px="150">
          <ConversionsCountNote days={CONVERSION_COUNT_DAYS} />
        </Box>
      </DataTable>
    </>
  );
}

function conversionViewHref(pathname: string, searchStr: string, view: ConversionView): string {
  const params = new URLSearchParams(searchStr);
  params.delete('page');

  if (view === 'all') {
    params.delete('view');
  } else {
    params.set('view', view);
  }

  const query = params.toString();
  return query === '' ? pathname : `${pathname}?${query}`;
}

function useConversionColumns(
  workspaceId: string,
  terms: ConversionUsageTerms,
): ColumnDef<DataTableFeatures, Conversion>[] {
  return useMemo(
    () => [
      {
        id: 'name',
        header: 'Conversion',
        size: 400,
        minSize: 300,
        maxSize: 480,
        enableHiding: false,
        cell: ({ row }) => (
          <ConversionNameCell conversion={row.original} href={goalPath(workspaceId, row.original.id)} />
        ),
      },
      // Narrow, and next to the name: it is the row's own state, which is
      // where it sat before it had a column, and the widest thing it ever
      // says is "Not tracking".
      {
        id: 'status',
        header: 'Status',
        size: 150,
        minSize: 130,
        maxSize: 170,
        cell: ({ row }) => <ConversionStatusCell conversion={row.original} />,
      },
      {
        id: 'counted-when',
        header: 'Counted when',
        size: 280,
        minSize: 220,
        maxSize: 340,
        cell: ({ row }) => <ConversionCountedWhenCell conversion={row.original} />,
      },
      {
        id: 'conversions',
        header: `Conversions (last ${String(CONVERSION_COUNT_DAYS)} days)`,
        size: 250,
        minSize: 210,
        maxSize: 290,
        cell: () => <ConversionCountCell />,
      },
      {
        id: 'impact',
        header: 'Impact',
        size: 180,
        minSize: 150,
        maxSize: 220,
        cell: ({ row }) => (
          <ConversionImpactCell conversion={row.original} href={goalPath(workspaceId, row.original.id)} />
        ),
      },
      {
        id: 'sources',
        header: 'Used by',
        size: 220,
        minSize: 180,
        maxSize: 280,
        cell: ({ row }) => <ConversionUsageCell conversion={row.original} terms={terms} />,
      },
    ],
    [terms, workspaceId],
  );
}
